import { Box, Button, Paper, Typography } from '@mui/material';
import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { NavLink as RouterLink, Route, Switch } from 'react-router-dom';
import { Observable } from 'rxjs';

import { If } from 'app/components/if';
import { NotFound } from 'app/components/not-found';
import { Translate } from 'app/components/translate';
import { CrisRequest } from 'app/model/cris-request';
import { crisRequestService } from 'app/services/cris-request.service';
import { RootState } from 'app/store';
import { RouteWithIdParam } from 'app/type';

import { CrisActions } from './cris-actions';
import { CrisDelete } from './cris-delete';
import { CrisExecution } from './cris-execution';
import { CrisSummary } from './cris-summary';
import { RequestMenu } from './request-menu';
import {
    executeCrisRequest,
    loadCrisRequest,
    saveCrisRequest,
    selectCurrentRequest,
    setCurrentCrisRequest,
} from '../cris.slice';

interface CrisRequestRouterProps extends RouteWithIdParam {
    // redux props
    currentRequest?: CrisRequest;

    // redux actions
    setCurrentCrisRequest: (request?: CrisRequest) => void;

    saveCrisRequest: (data: CrisRequest) => Observable<CrisRequest>;

    executeCrisRequest: (data: CrisRequest) => Observable<CrisRequest>;

    loadCrisRequest: (id: string) => Observable<CrisRequest>;
}

class CrisRequestRouter extends React.Component<CrisRequestRouterProps> {
    public componentDidMount() {
        const request = this.props.currentRequest;

        if (request && request.id === this.props.match.params.id) {
            return;
        }

        if (request) {
            this.props.setCurrentCrisRequest(); // ids did not match, clear the request and reload it
        }

        this.props.loadCrisRequest(this.props.match.params.id).subscribe((value) => {
            this.props.setCurrentCrisRequest(value);
        });
    }

    public render() {
        const currentRequest = this.props.currentRequest;

        if (!currentRequest) {
            return ''; // render nothing while loading
        }

        return (
            <React.Fragment>
                <Box display='flex'>
                    <RequestMenu selectedRequest={currentRequest} />
                    <Box flexGrow={1}>
                        <Switch>
                            <Route exact path='/cris/request/:id'>
                                <CrisSummary request={currentRequest} />
                            </Route>
                            <Route exact path='/cris/request/:id/summary'>
                                <Typography variant='h3'>
                                    <Translate tkey='headings:Data update request created' />
                                </Typography>
                                <CrisSummary request={currentRequest} />
                                <If condition={!!currentRequest.jiraLink}>
                                    <Box
                                        component={Paper}
                                        p={2}
                                        bgcolor='warning.light'
                                        my={3}
                                        display='flex'
                                        justifyContent='center'
                                    >
                                        <Typography variant='h5' className='my-1'>
                                            <Translate tkey='cris.request.jira.disclaimer.3' />
                                        </Typography>
                                    </Box>
                                </If>

                                <Box my={3} display='flex' justifyContent='center'>
                                    <Button
                                        type='button'
                                        variant='contained'
                                        color='secondary'
                                        className='mb-1'
                                        component={RouterLink}
                                        to='/cris'
                                    >
                                        Return Home
                                    </Button>
                                </Box>
                            </Route>
                            <Route exact path='/cris/request/:id/execute'>
                                <CrisExecution
                                    request={currentRequest}
                                    onExecute={() => {
                                        this.props.executeCrisRequest(currentRequest).subscribe((updatedRequest) => {
                                            this.props.setCurrentCrisRequest(updatedRequest);
                                            this.props.history.push(`/cris/request/${currentRequest.id}`);
                                        });
                                    }}
                                />
                            </Route>
                            <Route exact path='/cris/request/:id/delete'>
                                <CrisDelete
                                    request={currentRequest}
                                    onDelete={(target) => {
                                        if (!target) {
                                            return;
                                        }
                                        crisRequestService
                                            .deleteAndTransfer(currentRequest, target.id)
                                            .subscribe(() => {
                                                this.props.setCurrentCrisRequest(target);
                                                this.props.history.push(`/cris/request/${target.id}`);
                                            });
                                    }}
                                />
                            </Route>
                            <Route exact path='/cris/request/:id/actions'>
                                <CrisActions
                                    request={currentRequest}
                                    onChange={(request) => {
                                        this.props.setCurrentCrisRequest(request);
                                    }}
                                    onSave={(request, moveNext) => {
                                        this.props.saveCrisRequest(request).subscribe((updatedRequest) => {
                                            this.props.setCurrentCrisRequest(updatedRequest);
                                            if (moveNext) {
                                                this.props.history.push(`/cris/request/${currentRequest.id}/execute`);
                                            }
                                        });
                                    }}
                                />
                            </Route>
                            <Route path=''>
                                <NotFound />
                            </Route>
                        </Switch>
                    </Box>
                </Box>
            </React.Fragment>
        );
    }
}

const mapStateToProps = (state: RootState) => ({
    currentRequest: selectCurrentRequest(state),
});

const thunks = { setCurrentCrisRequest, loadCrisRequest, saveCrisRequest, executeCrisRequest };
const connected = connect(mapStateToProps, thunks)(withRouter(CrisRequestRouter));

export { connected as CrisRequestRouter };
